import { FormHelperTextProps, Grid, TextField } from '@material-ui/core';
import { ChangeEvent, useEffect, useState } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { useStyles } from './MovieEditor.styles';

const TITLE_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 500;
const URL_MAX_LENGTH = 1000;

interface MovieEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (movieId: string, title: string, description: string, url: string) => void;
  onChange: () => void;
  movieTitle: string;
  movieDescription: string;
  movieUrl: string;
  movieId: string;
}

export const MovieEditor: React.FC<MovieEditorProps> = ({
  title,
  onCancel,
  onSave,
  movieTitle: titleProp,
  movieDescription: descriptionProp,
  movieUrl: urlProp,
  movieId: movieIdProp,
}) => {
  const classes = useStyles();
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);

  const [currMovieTitle, setCurrMovieTitle] = useState<string>(titleProp ?? '');
  const [description, setDescription] = useState<string>(descriptionProp ?? '');
  const [url, setUrl] = useState<string>(urlProp ?? '');

  const [hasTitleFocus, setHasTitleFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [hasUrlFocus, setHasUrlFocus] = useState<boolean>(false);

  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const handleTitleChange = (value: string) => {
    setCurrMovieTitle(value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
  };

  useEffect(() => {
    setIsDataChanged(titleProp !== currMovieTitle || description !== descriptionProp || url !== urlProp);
  }, [titleProp, currMovieTitle, description, descriptionProp, url, urlProp]);

  const isTitleTooLong = currMovieTitle.length > TITLE_MAX_LENGTH;
  const isDiscriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH;
  const isUrlTooLong = url.length > URL_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="xs"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(currMovieTitle, description, url, movieIdProp)}
        confirmButtonDisabled={
          !currMovieTitle ||
          !description ||
          !url ||
          isTitleTooLong ||
          isDiscriptionTooLong ||
          isUrlTooLong ||
          !isDataChanged
        }
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <TextField
              value={currMovieTitle}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleTitleChange(e.target.value)}
              label="Tytuł*"
              variant="outlined"
              fullWidth
              onFocus={() => setHasTitleFocus(true)}
              onBlur={() => setHasTitleFocus(false)}
              error={isTitleTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isTitleTooLong ? 'Podane pytanie jest za długie' : ''}
                  limitsVisible={hasTitleFocus || isTitleTooLong}
                  valueLength={currMovieTitle.length}
                  limit={TITLE_MAX_LENGTH}
                  withError={isTitleTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.helperRow}></Grid>
          <Grid item xs={12}>
            <TextField
              value={description}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleDescriptionChange(e.target.value)}
              label="Opis*"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isDiscriptionTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isDiscriptionTooLong ? 'Podana odpowiedź jest za długa' : ''}
                  limitsVisible={hasDescriptionFocus || isDiscriptionTooLong}
                  valueLength={description.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isDiscriptionTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.helperRow}></Grid>
          <Grid item xs={12}>
            <TextField
              value={url}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleUrlChange(e.target.value)}
              label="Link*"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              onFocus={() => setHasUrlFocus(true)}
              onBlur={() => setHasUrlFocus(false)}
              error={isUrlTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isUrlTooLong ? 'Podana odpowiedź jest za długa' : ''}
                  limitsVisible={hasUrlFocus || isUrlTooLong}
                  valueLength={url.length}
                  limit={URL_MAX_LENGTH}
                  withError={isUrlTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.helperRow}></Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
